import { useMemo } from 'react';
import { useSettings } from '../data/useSettings';
import {
  studentDisplayName, studentSortKey, billingContactName, teacherDisplayName, teacherNameParts, teacherFullName,
} from '../domain/people';

// The studio's Naming Format, already applied.
//
// domain/people.js takes the format as an argument, which is right for a pure
// module; bound once here, a call site cannot forget it and render "Okafor,
// Ada" next to "Ada Okafor".
//
// Which of the three roles applies is a property of *who is being named*, not
// of the screen — a student is written in the Student format wherever it
// appears — so that mapping lives here too rather than at the call sites.
export default function useNames() {
  const { nameFormats, settings } = useSettings();
  const teacherName = settings?.teacher;

  return useMemo(() => ({
    // A student, in the Student Name Format.
    student: (student, fallback = 'Student') =>
      studentDisplayName(student, fallback, nameFormats.student),

    // What a list of students sorts on. Follows the same format, so the order
    // matches what is on the screen.
    studentSortKey: (student) => studentSortKey(student, nameFormats.student),

    // The person a family account's post is addressed to, in the Parent Name
    // Format.
    //
    // `contact` is a `familyContacts` record (#319), from
    // `useFamilies().billingContactFor(familyId)`. Required rather than
    // optional for the reason `billingContactName` gives: an omitted one
    // renders the family's surname where a person's name belongs.
    familyContact: (family, contact, fallback = 'Contact') =>
      billingContactName(family, contact, fallback, nameFormats.parent),

    // The teacher, in the Teacher Name Format (#797). `displayName` is the
    // signed-in user's, the fallback for a studio that has stored no name
    // (`teacherDisplayName` in domain/people.js); passed in rather than read
    // here so this hook needs nothing but the settings.
    teacher: (displayName = null) => teacherDisplayName({ teacher: teacherName, nameFormats }, displayName),

    // The teacher's first and last names, and the full name as First Last,
    // for the places MMS does not apply the format.
    teacherParts: (displayName = null) => teacherNameParts({ teacher: teacherName }, displayName),
    teacherFullName: (displayName = null) => teacherFullName({ teacher: teacherName }, displayName),

    // The raw formats, for the two places that need to hand them to a domain
    // function rather than render a name (the cancellation dialog and the
    // report runner).
    formats: nameFormats,
  }), [nameFormats, teacherName]);
}
